import { useLiteStorefront } from '@hooks/use-lite-storefront';
import { useTrackStorefrontInteraction } from '@hooks/useTrackStorefrontInteraction';
import { useAppSelector } from '@redux/hooks';
import { selectIsMobile } from '@redux/viewport/selectors';
import { StatusBadge } from '@tkww/orion-web-ds-feedback';
import { Icon } from '@tkww/orion-web-ds-graphic';
import { TextBody, TextCategory } from '@tkww/orion-web-ds-text';
import { compose } from '@xo-union/react-css-modules';
import classNames from 'classnames';
import React, {
	type DetailedHTMLProps,
	type FC,
	type HTMLAttributes,
	useCallback,
	useMemo,
	useState,
} from 'react';
import type { DetailedPricingPackage } from '../../jotai/storefront/detailed-pricing';
import Styles from './package.scss';

interface PackageRowProps {
	detail: string;
	icon: string;
	iconColor: Icon.Color;
	expanded?: boolean;
	index: number;
}

const PackageRow = ({
	detail,
	icon,
	iconColor,
	expanded,
	index,
}: PackageRowProps) => {
	const isLiteStorefront = useLiteStorefront();

	return (
		<div
			className={classNames(Styles.detailRow, {
				[Styles.hide]: !expanded && index > 3,
				[Styles.serviceDuration]: icon === 'clock',
			})}
		>
			<Icon name={icon} size="sm" color={iconColor} aria-label={icon} />
			<TextBody
				size={isLiteStorefront ? 'md' : 'sm'}
				className={Styles.detailRowText}
			>
				{detail}
			</TextBody>
		</div>
	);
};

interface PricingDetailProps {
	pkg: DetailedPricingPackage;
	collapse: boolean;
}

const PackageBody: FC<PricingDetailProps> = ({ pkg, collapse }) => {
	const [expanded, setExpanded] = useState(false);

	return (
		<div
			className={classNames([Styles.detail, { [Styles.collapsed]: collapse }])}
		>
			{pkg.serviceDuration && (
				<PackageRow
					detail={pkg.serviceDuration}
					icon="clock"
					iconColor="dark"
					index={-1}
				/>
			)}
			{pkg.services.map((detail, index) => (
				<PackageRow
					key={`${detail.replace(' ', '-')}-${index}`}
					detail={detail}
					icon="check"
					iconColor="success"
					index={index}
					expanded={expanded}
				/>
			))}
			{pkg.services.length > 4 && (
				<TextBody
					as="button"
					onClick={() => setExpanded(!expanded)}
					className={Styles.seeAll}
					size="sm"
				>
					{expanded ? 'See less' : 'See all'}
				</TextBody>
			)}
		</div>
	);
};

interface PackageHeadingsProps {
	mobile: boolean;
	collapse?: boolean;
	toggleCollapse?: () => void;
	packageName?: string;
	startingAt?: string | null;
}

const PackageHeading: FC<PackageHeadingsProps> = ({
	packageName,
	startingAt,
	mobile,
	toggleCollapse,
	collapse,
}) => {
	const props: DetailedHTMLProps<
		HTMLAttributes<HTMLDivElement>,
		HTMLDivElement
	> = useMemo(() => {
		if (!mobile) {
			return {
				className: Styles.packageHeading,
			};
		}

		return {
			className: Styles.packageHeading,
			role: 'button',
			tabIndex: 0,
			onClick: toggleCollapse,
			onKeyPress: toggleCollapse,
			'aria-expanded': !collapse,
		};
	}, [mobile, toggleCollapse, collapse]);

	return (
		<div {...props}>
			{packageName && (
				<TextBody size="md" className={Styles.packageName} bold>
					{packageName}
				</TextBody>
			)}
			{startingAt && (
				<div className={Styles.packagePrice}>
					<TextCategory bold category="subhead" className={Styles.price}>
						{startingAt}
					</TextCategory>
					<TextBody size="sm">starting price</TextBody>
				</div>
			)}
			<Icon
				name={!collapse ? 'caret-up' : 'caret-down'}
				size="md"
				className={Styles.collapseButton}
				aria-label={!collapse ? 'Collapse details' : 'Expand details'}
			/>
		</div>
	);
};

export interface PackageProps {
	initialCollapse: boolean;
	pkg: DetailedPricingPackage;
}

export const Package = ({ initialCollapse, pkg }: PackageProps) => {
	const [collapse, setCollapse] = useState(initialCollapse);
	const track = useTrackStorefrontInteraction('Packages');
	const isMobile = useAppSelector((state) => selectIsMobile(state));

	const toggleCollapse = useCallback(() => {
		setCollapse((value) => !value);
		track('Click arrow', {
			action: collapse ? 'Expand' : 'Collapse',
		});
	}, [track, collapse]);

	return (
		<div className={Styles.packageContainer}>
			{pkg.mostPopular && (
				<StatusBadge
					styleType="featured"
					size="sm"
					classes={compose({ container: Styles.packageBadge })}
				>
					Most popular
				</StatusBadge>
			)}
			<div className={Styles.packageWrapper}>
				<PackageHeading
					mobile={isMobile}
					collapse={collapse}
					toggleCollapse={toggleCollapse}
					packageName={pkg.title}
					startingAt={pkg.price}
				/>
				<hr
					className={classNames([
						Styles.divider,
						{ [Styles.collapsed]: collapse },
					])}
				/>
				<PackageBody pkg={pkg} collapse={collapse} />
			</div>
		</div>
	);
};
